import * as React from "react";
import { Button, Form, Modal, InputGroup } from "react-bootstrap";

import { Loader } from "lib/components/Loader";
import { RolePopup } from "lib/components/RolePopup";
import I18n from "lib/I18n";
import style from "./style.module.css";

import { Role, Roles } from "../../constants";
import { BsArrowRepeat } from "react-icons/bs";
import { getStore } from "../../store";
import { observer } from "mobx-react-lite";
import { BootstrapTable } from "lib/components/BootstrapTable";

const flexItemStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
};

export const UnregisteredPane = observer(() => {
  const store = getStore().unregistered;

  const onSearch = () => {
    store.listUserTitleRoles();
  };

  const onRowSelect = (row: { loginid: string }, isSelected: boolean) => {
    store.selectUsers([row.loginid], isSelected);
    return true;
  };

  const onSelectAll = (rows: { loginid: string }[], isSelected: boolean) => {
    store.selectUsers(
      rows.map((row) => row.loginid),
      isSelected,
    );
    return true;
  };

  const onForceCreateRoles = async () => {
    await store.createUserTitleRole();
    store.alertShow = false;
  };

  const onCreateRoles = () => {
    if (store.shouldShowAlert()) {
      store.alertShow = true;
    } else {
      store.createUserTitleRole();
    }
  };

  const onHideModal = () => {
    store.alertShow = false;
  };

  const renderTable = () => {
    return (
      <BootstrapTable
        data={store.userTitleRoles}
        columns={[
          {
            dataField: "name",
            label: I18n.t("user_title_role_editor.label.name"),
            placeholder: `${I18n.t("user_title_role_editor.label.name")}...`,
          },
          {
            dataField: "loginid",
            label: I18n.t("user_title_role_editor.label.loginid"),
            placeholder: `${I18n.t("user_title_role_editor.label.loginid")}...`,
          },
        ]}
        idField="loginid"
        selectedIds={store.userTitleRoles
          .filter((_) => _.isSelected)
          .map((_) => _.loginid)}
        onRowSelect={onRowSelect}
        onSelectAll={onSelectAll}
      />
    );
  };

  const renderModal = () => {
    return (
      <Modal
        animation={false}
        size="lg"
        show={store.alertShow}
        onHide={onHideModal}
      >
        <Modal.Header>
          <Modal.Title>
            {I18n.t("user_title_role_editor.label.confirm")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflow: "auto" }}>
          {renderModalBody()}
        </Modal.Body>
        <Modal.Footer>{renderModalFooter()}</Modal.Footer>
      </Modal>
    );
  };

  const renderModalBody = () => {
    const userNames = store
      .notNclUsers()
      .map((_) => _.name)
      .concat(store.notNclNdids());
    return (
      <div>
        <p>{I18n.t("user_title_role_editor.messages.role_warning1")}</p>
        <ul>
          {userNames.map((userName, i) => (
            <li key={i}>{userName}</li>
          ))}
        </ul>
        <p>{I18n.t("user_title_role_editor.messages.role_warning2")}</p>
      </div>
    );
  };

  const renderModalFooter = () => {
    return (
      <div>
        <Button variant="primary" onClick={onForceCreateRoles}>
          {I18n.t("user_title_role_editor.action.create")}
        </Button>
        <Button variant="light" onClick={onHideModal}>
          {I18n.t("user_title_role_editor.action.cancel")}
        </Button>
      </div>
    );
  };

  const canCreate =
    (store.ndids.length > 0 ||
      store.userTitleRoles.some((_) => _.isSelected)) &&
    !store.isCreating &&
    !store.isLoading;

  return (
    <div className={style.root}>
      <Form.Group className={style.tableSection}>
        <Form.Label>
          {I18n.t("user_title_role_editor.label.unregistered_users")}
        </Form.Label>
        <p>{I18n.t("user_title_role_editor.messages.unregistered")}</p>
        <InputGroup className="mb-2">
          <Form.Control
            type="text"
            value={store.loginidFilter}
            placeholder="NDID"
            onChange={(e) => (store.loginidFilter = e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && onSearch()}
          />
          <Button variant="light" onClick={onSearch} disabled={store.isLoading}>
            <BsArrowRepeat />
            {I18n.t("user_title_role_editor.action.reload")}
          </Button>
        </InputGroup>
        <Loader
          show={store.isLoading}
          style={{ ...flexItemStyle, minHeight: 0, overflow: "hidden" }}
        >
          {renderTable()}
        </Loader>
      </Form.Group>
      <Form.Group>
        <Form.Label>
          {I18n.t("user_title_role_editor.label.loginid")}
        </Form.Label>
        <Form.Control
          type="text"
          value={store.ndids}
          placeholder={I18n.t("user_title_role_editor.placeholder.loginid")}
          onChange={(e) => (store.ndids = e.currentTarget.value)}
        />
        <Form.Text muted>
          {I18n.t("user_title_role_editor.messages.ndid")}
        </Form.Text>
      </Form.Group>
      <Form.Group>
        <Form.Label>{I18n.t("user_title_role_editor.label.role")}</Form.Label>
        <RolePopup placement="top" />
        <Form.Select
          onChange={(e) => (store.role = e.currentTarget.value as Role)}
        >
          {Roles.map((role, i) => (
            <option value={role} key={i}>
              {I18n.t(`roles.${role}`)}
            </option>
          ))}
        </Form.Select>
        <Form.Text muted>
          {I18n.t("user_title_role_editor.messages.role")}
        </Form.Text>
      </Form.Group>
      <Form.Group>
        <Button variant="primary" onClick={onCreateRoles} disabled={!canCreate}>
          {I18n.t("user_title_role_editor.action.create")}
        </Button>
      </Form.Group>
      <div>{renderModal()}</div>
    </div>
  );
});
